import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Divider,
} from '@mui/material';

const formatMoney = (n?: number) =>
  typeof n === "number"
    ? `$${n.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}` : "-";

interface OrderTotalProps {
  itemsCount: number;
  discountCode: string;
  reachedPromoCodeMaxDiscount: boolean;
  redeemedRewardPoints: number;
  orderTotal: number;
  maxApplicableRewardPoints?: number;
  discount: number;
}

const OrderTotal: React.FC<OrderTotalProps> = ({
  itemsCount,
  discountCode,
  reachedPromoCodeMaxDiscount,
  redeemedRewardPoints,
  orderTotal,
  maxApplicableRewardPoints,
  discount,
}) => {
  const [showDiscountSection, setShowDiscountSection] = useState(false);
  const [showSubtotal, setShowSubtotal] = useState(false);
  const [rewardTotal, setRewardTotal] = useState<string>('');

  useEffect(() => {
    const couponExists = discount > 0;
    const hasRewardPoints = redeemedRewardPoints > 0;

    if (couponExists) {
      setShowDiscountSection(true);
    }

    if (hasRewardPoints) {
      setRewardTotal(formatMoney(redeemedRewardPoints));
    }

    setShowSubtotal(couponExists || hasRewardPoints);
  }, [discount, redeemedRewardPoints]);

  return (
    <Box className="total-info order-sub-total-container">
      <Typography variant="h6" fontSize={24} color={"#737373"}>Your Order Subtotal</Typography>
      <Divider className="break-line" sx={{ my: 2 }} />

      <Box className="order-subtotal" display="flex" justifyContent="space-between">
        <Typography>
          {itemsCount === 1
            ? `${itemsCount} Item`
            : `${itemsCount} Items`}
        </Typography>
        <Typography className="value-summary">{formatMoney(orderTotal)}</Typography>
      </Box>

      {showDiscountSection && (
        <Box mt={1} display="flex" flexDirection="column">
          <Box color={"#29b05f"} display="flex" justifyContent="space-between">
            <Typography fontWeight={600}>{`Promo Savings [${discountCode}]`}</Typography>
            <Typography fontWeight={600} className="value-summary">-{formatMoney(discount)}</Typography>
          </Box>
        </Box>
      )}

      {reachedPromoCodeMaxDiscount && (
        <Typography color="error" mt={1} className="checkout-message">
          You have reached the maximum discount for this promo code
        </Typography>
      )}

      {rewardTotal &&  redeemedRewardPoints > 0 && (
        <Box mt={1} display="flex" justifyContent="space-between">
          <Typography>Impact Rewards</Typography>
          <Typography color="error" className="value-summary">
            -{rewardTotal}
          </Typography>
        </Box>
      )}

      <Divider className="break-line" sx={{ my: 2 }} />

      {showSubtotal && (
        <Box mb={1} display="flex" justifyContent="space-between">
          <Typography color={"#737373"} fontWeight={600} fontSize={20}>Subtotal</Typography>
          <Typography className="value-summary">{formatMoney(orderTotal - redeemedRewardPoints - discount)}</Typography>
        </Box>
      )}

      {maxApplicableRewardPoints != null && maxApplicableRewardPoints > 0 && (
        <Box mt={1} display="flex" justifyContent="space-between">
          <Typography color={"#737373"}>Impact Rewards</Typography>
          <Typography className="probucks-value" color={"#64A70B"} fontSize={17}>{formatMoney(maxApplicableRewardPoints - redeemedRewardPoints)} Available for This Order</Typography>
        </Box>
      )}

      <Typography className="checkout-message" mt={1} style={{ fontSize: 10, fontWeight: 400, color: "#f00", fontStyle: 'italic' }}>
        Applicable taxes or service charges are not reflected until your order is processed.
        <br />
        Prices are subject to change by branch.
      </Typography>
    </Box>
  );
};

export default OrderTotal;
